import { useEffect, useState, useCallback } from 'react';

const STORAGE_KEY = 'mi-theme';

function getInitialTheme() {
  if (typeof window === 'undefined') return 'light';
  const saved = localStorage.getItem(STORAGE_KEY);
  if (saved === 'light' || saved === 'dark') return saved;
  return 'light';
}

function applyTheme(theme) {
  const root = document.documentElement;
  if (theme === 'dark') {
    root.classList.add('mi-dark');
  } else {
    root.classList.remove('mi-dark');
  }
}

export function useTheme() {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem(STORAGE_KEY, theme);
    // Notify other tabs/components in this tab that don't share state
    window.dispatchEvent(new CustomEvent('mi-theme-change', { detail: { theme } }));
  }, [theme]);

  const toggle = useCallback(() => {
    setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
  }, []);

  return { theme, toggle, setTheme };
}

/**
 * Passive hook for components that only need to *read* the current theme
 * (e.g. Logo to swap dark/light asset). Listens to the custom event
 * dispatched by useTheme and to native storage events from other tabs.
 */
export function useCurrentTheme() {
  const [theme, setTheme] = useState(getInitialTheme);
  useEffect(() => {
    const onLocal   = (e) => setTheme(e.detail.theme);
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && (e.newValue === 'light' || e.newValue === 'dark')) {
        setTheme(e.newValue);
      }
    };
    window.addEventListener('mi-theme-change', onLocal);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('mi-theme-change', onLocal);
      window.removeEventListener('storage', onStorage);
    };
  }, []);
  return theme;
}

/** Apply theme once on boot (before React mounts) to avoid flash. */
export function initThemeOnBoot() {
  applyTheme(getInitialTheme());
}
